import React, { FC, useState } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { subMinutes, subYears } from 'date-fns';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { useLocalStorage } from 'react-use';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { accountService, transactionService } from '../../../../channels/secure-web/services';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../userProfile/src/contexts/UserInfo/hooks';
import { AliasProvider } from '../../../account/src/contexts/Alias/AliasProvider';
import { AccountList } from '../../../account/src/domain/Account/entities';
import { useGetCards } from '../../customHooks';
import { useIsCardHolder, useCardHeaders } from '../../../transaction/src/contexts/Parameters/hooks';
import { CardStatuses } from '../../../card/src/types';
import { DashboardContext } from './DashboardContext';

const DashboardProvider: FC = ({ children }) => {
  // Get initial state values
  const [popularCards] = useLocalStorage<GenericObject>('popular-cards', {});
  const [timestamp] = useState(new Date().toISOString());
  const customerId = useSelectedCustomerId();
  const isCardHolderOnly = useIsCardHolder();
  const cardHeaders = useCardHeaders();
  const isTransferHistoryAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.FundsTransferHistory);
  const {
    cardList: cardListAllData,
    isLoadingCardList: isLoadingCardListAllData,
    cardListError: cardListAllDataError,
  } = useGetCards(
    {
      paymentCardStatuses: [
        CardStatuses.Active,
        CardStatuses.Inactive,
        CardStatuses.Blocked,
        CardStatuses.Pin_Tries_Exceeded,
      ],
    },
    { pageNumber: 0, pageSize: 1 },
  );
  const {
    cardList: popularCardListData,
    isLoadingCardList: isLoadingPopularCardListData,
    cardListError: popularCardListDataError,
  } = useGetCards(
    {
      paymentCardStatuses: [
        CardStatuses.Active,
        CardStatuses.Inactive,
        CardStatuses.Blocked,
        CardStatuses.Pin_Tries_Exceeded,
      ],
    },
    { pageNumber: 0, pageSize: 50 },
  );

  const shouldFetchAccounts = !isEmpty(customerId) && !isEmpty(cardHeaders) && !isCardHolderOnly;
  const {
    data: accountsData,
    error: accountsDataError,
    isLoading: isLoadingAccounts,
    mutate: accountsDataMutator,
  } = useInvokeSWRService<AccountList>(
    accountService.GetCustomerAccounts,
    {
      customerId,
      retrieveSummary: true,
      meta: {
        timestamp,
      },
    },
    shouldFetchAccounts,
  );

  const shouldFetchConsolidatedTransactions = !isEmpty(customerId) && isTransferHistoryAllowed && !isCardHolderOnly;
  const {
    data: consolidatedTransactionListData,
    error: consolidatedTransactionListError,
    isLoading: isLoadingConsolidatedTransactionList,
  } = useInvokeSWRService(
    transactionService.GetConsolidatedTransactions,
    {
      fromTrxDate: stringToDate({
        date: subYears(new Date(), 1).toISOString(),
        formatExp: "yyyy-MM-dd'T'00:00:00",
      }),
      toTrxDate: stringToDate({
        date: subMinutes(new Date(), 1).toISOString(),
        formatExp: "yyyy-MM-dd'T'23:59:59",
        tz: 'UTC',
      }),
      valueDateFrom: undefined,
      valueDateTo: undefined,
      fromAmount: '0.00',
      toAmount: '9999999999.99',
      accountNumber: undefined,
      customerId,
      pageSize: 10,
      pageOffset: 0,
      meta: {
        timestamp,
      },
    },
    shouldFetchConsolidatedTransactions,
  );

  return (
    <DashboardContext.Provider
      value={{
        popularCardListData: popularCardListData?.cards ?? null,
        cardsListAllItems: cardListAllData?.pagination?.totalElements ?? 0,
        accountListData: accountsData,
        consolidatedTransactionListData,
        popularCards,
        loadingState: {
          isLoadingAccounts,
          isLoadingPopularCardListData,
          isLoadingCardListAllData,
          isLoadingConsolidatedTransactionList,
        },
        errorState: {
          accountsDataError,
          popularCardListDataError,
          cardListAllDataError,
          consolidatedTransactionListError,
        },
      }}>
      <AliasProvider customerId={customerId} accounts={accountsData?.accounts} accountsRefetch={accountsDataMutator}>
        {children}
      </AliasProvider>
    </DashboardContext.Provider>
  );
};

export { DashboardProvider };
